import React from "react";
import { Link } from "react-router-dom";
import {
  Cloud,
  ServerCog,
  ShieldCheck,
  Workflow,
  ArrowRight,
  Activity,
  Network,
  Database,
  Lock,
  Cpu,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { GlowOrb, GridBg, Overline } from "../components/SectionDecor";

const Home = () => {
  const { t } = useLanguage();

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <GridBg />
        <GlowOrb color="#00d4ff" size={520} opacity={0.18} className="-top-32 -left-32" />
        <GlowOrb color="#00ffb3" size={440} opacity={0.14} className="top-40 right-0" />

        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-28 pb-28 lg:pt-36 lg:pb-40">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 smk-fade-up">
              <Overline>{t.hero.overline}</Overline>
              <h1 className="mt-5 text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.05]">
                {t.hero.title1}{" "}
                <span className="smk-gradient-text">{t.hero.titleAccent}</span>{" "}
                {t.hero.title2}
              </h1>
              <p className="mt-7 text-base sm:text-lg text-zinc-400 max-w-2xl leading-relaxed">
                {t.hero.subtitle}
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-cta-primary"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full smk-gradient-bg text-black font-semibold hover:brightness-110 transition"
                >
                  {t.hero.ctaPrimary}
                  <ArrowRight size={16} />
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 text-white hover:border-[#00d4ff]/50 hover:bg-white/5 transition"
                >
                  {t.hero.ctaSecondary}
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 smk-fade-up" style={{ animationDelay: "120ms" }}>
              <div className="relative smk-card-highlight rounded-2xl p-7">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-[#00ffb3] smk-pulse-dot" />
                    <span className="font-mono text-xs text-zinc-400">smaktech.cloud / status</span>
                  </div>
                  <span className="font-mono text-[10px] text-[#00d4ff]">LIVE</span>
                </div>

                <div className="mt-6 space-y-4">
                  {[
                    { icon: Cloud, label: "Cloud Platform", value: "OPERATIONAL" },
                    { icon: Network, label: "Network Mesh", value: "OPERATIONAL" },
                    { icon: Database, label: "Storage Cluster", value: "OPERATIONAL" },
                    { icon: ShieldCheck, label: "Security Layer", value: "OPERATIONAL" },
                  ].map(({ icon: Icon, label, value }) => (
                    <div key={label} className="flex items-center justify-between border-b border-white/5 pb-3 last:border-0">
                      <div className="flex items-center gap-3">
                        <Icon size={16} className="text-[#00d4ff]" />
                        <span className="text-sm text-zinc-300">{label}</span>
                      </div>
                      <span className="font-mono text-[10px] text-[#00ffb3]">{value}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-5 border-t border-white/5 grid grid-cols-3 gap-3">
                  <div>
                    <div className="font-mono text-[10px] text-zinc-500">UPTIME</div>
                    <div className="text-lg font-semibold smk-gradient-text">99.99%</div>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] text-zinc-500">LATENCY</div>
                    <div className="text-lg font-semibold text-white">12ms</div>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] text-zinc-500">NODES</div>
                    <div className="text-lg font-semibold text-white">42</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats row */}
          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { v: t.hero.stat1, l: t.hero.stat1Label, icon: Cpu },
              { v: t.hero.stat2, l: t.hero.stat2Label, icon: Activity },
              { v: t.hero.stat3, l: t.hero.stat3Label, icon: Lock },
            ].map((s, i) => (
              <div key={i} className="smk-card rounded-xl p-6">
                <s.icon size={18} className="text-[#00d4ff]" />
                <div className="mt-3 text-2xl font-semibold smk-gradient-text">{s.v}</div>
                <div className="mt-1 text-sm text-zinc-500">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES TEASER */}
      <section className="relative py-24 lg:py-32">
        <GlowOrb color="#00ffb3" size={360} opacity={0.08} className="top-40 -right-20" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <div className="max-w-3xl">
            <Overline>{t.services.overline}</Overline>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              {t.services.title}
            </h2>
            <p className="mt-4 text-zinc-400">{t.services.subtitle}</p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6">
            {[
              { key: "consulting", icon: Cloud, span: "md:col-span-7" },
              { key: "supervision", icon: Activity, span: "md:col-span-5" },
              { key: "infra", icon: ServerCog, span: "md:col-span-5" },
              { key: "tools", icon: Workflow, span: "md:col-span-7" },
            ].map(({ key, icon: Icon, span }) => {
              const item = t.services.items[key];
              return (
                <div key={key} className={`smk-card rounded-2xl p-8 ${span}`} data-testid={`service-card-${key}`}>
                  <div className="h-10 w-10 rounded-lg smk-gradient-bg flex items-center justify-center">
                    <Icon size={18} className="text-black" />
                  </div>
                  <h3 className="mt-5 text-xl font-semibold">{item.title}</h3>
                  <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{item.desc}</p>
                  <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                    {item.bullets.slice(0, 4).map((b) => (
                      <li key={b} className="text-sm text-zinc-300 flex items-start gap-2">
                        <span className="mt-1.5 h-1 w-1 rounded-full bg-[#00d4ff]" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          <div className="mt-12">
            <Link
              to="/services"
              data-testid="services-link"
              className="inline-flex items-center gap-2 text-sm text-[#00d4ff] hover:text-[#00ffb3] transition"
            >
              {t.common.learnMore}
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <div className="smk-card-highlight rounded-3xl p-12 lg:p-16 text-center relative overflow-hidden">
            <GlowOrb color="#00d4ff" size={320} opacity={0.12} className="-top-20 -left-20" />
            <GlowOrb color="#00ffb3" size={320} opacity={0.12} className="-bottom-20 -right-20" />
            <div className="relative">
              <Overline className="!text-[#00ffb3]">SMAKTECH</Overline>
              <h2 className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight">
                Prêt à <span className="smk-gradient-text">moderniser</span> votre infrastructure ?
              </h2>
              <p className="mt-4 text-zinc-400 max-w-2xl mx-auto">
                {"Discutons de votre projet cloud, de votre architecture cible ou de l'exploitation de vos environnements."}
              </p>
              <Link
                to="/contact"
                data-testid="home-cta-bottom"
                className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full smk-gradient-bg text-black font-semibold hover:brightness-110 transition"
              >
                {t.nav.cta}
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
